import React from 'react';
import { Document, Page } from 'react-pdf';
import { FileText } from 'lucide-react';

export interface SpecsModeViewerProps {
  file: { url: string; name?: string } | null;
  estimationId?: string;
  specsPageNumber: number;
  specsNumPages: number | null;
  specsScale: number;
  specsPageRotations: Record<number, number>;
  setSpecsNumPages: (n: number) => void;
  setSpecsPageNumber: (n: number | ((p: number) => number)) => void;
  setSpecsPageRotations: (v: Record<number, number> | ((p: Record<number, number>) => Record<number, number>)) => void;
  highlightBoundingBox: { top: number; left: number; width: number; height: number } | null;
  pdfOptions: Record<string, unknown>;
}

export const SpecsModeViewer: React.FC<SpecsModeViewerProps> = ({
  file,
  estimationId,
  specsPageNumber,
  specsNumPages,
  specsScale,
  specsPageRotations,
  setSpecsNumPages,
  setSpecsPageNumber,
  setSpecsPageRotations,
  highlightBoundingBox,
  pdfOptions,
}) => {
  if (!file) return null;

  return (
    <div
      className="pdf-annotator-wrapper"
      style={{
        background: 'var(--border-color)',
        padding: '2rem'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        <div className="pdf-page-container">
          <Document
            key="specs-document"
            file={file.url}
            options={pdfOptions}
            onLoadSuccess={(pdf: any) => {
              setSpecsNumPages(pdf.numPages);
              if (estimationId && file?.name) {
                const storageKey = `specs-page-number-${estimationId}-${file.name}`;
                const savedPage = localStorage.getItem(storageKey);
                if (savedPage) {
                  const pageNum = parseInt(savedPage, 10);
                  if (pageNum >= 1 && pageNum <= pdf.numPages) {
                    setSpecsPageNumber(pageNum);
                    console.log(`📖 Restored saved specs page number: ${pageNum}`);
                  } else {
                    setSpecsPageNumber(1);
                  }
                } else {
                  setSpecsPageNumber(1);
                }
              } else {
                setSpecsPageNumber(1);
              }
            }}
            loading={
              <div style={{
                position: 'fixed',
                transform: 'translateX(-15%)',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'var(--bg-primary)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                zIndex: 9999
              }}>
                <div style={{
                  animation: 'pulse-blue 2s ease-in-out infinite'
                }}>
                  <FileText size={48} color="#3b82f6" />
                </div>
                <p style={{
                  fontSize: '0.875rem',
                  color: 'var(--text-muted)',
                  margin: 0
                }}>Loading specifications...</p>
              </div>
            }
            error={
              <div className="pdf-error">
                <p>Failed to load specifications PDF</p>
              </div>
            }
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <Page
                pageNumber={specsPageNumber}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                scale={specsScale}
                rotate={specsPageRotations[specsPageNumber] || 0}
                onLoadSuccess={(page: any) => {
                  const nativeRotation = page.rotate || 0;
                  const savedRotation = specsPageRotations[specsPageNumber];

                  if (savedRotation === undefined && nativeRotation !== 0) {
                    console.log(`📄 Specs page ${specsPageNumber} has native rotation: ${nativeRotation}°`);
                    setSpecsPageRotations(prev => {
                      const updatedRotations = {
                        ...prev,
                        [specsPageNumber]: nativeRotation
                      };

                      if (estimationId && file?.name) {
                        try {
                          const storageKey = `specs-rotations-${estimationId}-${file.name}`;
                          localStorage.setItem(storageKey, JSON.stringify(updatedRotations));
                        } catch { /* quota exceeded — ignore */ }
                      }

                      return updatedRotations;
                    });
                  }
                }}
              />

              {highlightBoundingBox && (
                <div
                  style={{
                    position: 'absolute',
                    top: `${highlightBoundingBox.top * 100}%`,
                    left: `${highlightBoundingBox.left * 100}%`,
                    width: `${highlightBoundingBox.width * 100}%`,
                    height: `${highlightBoundingBox.height * 100}%`,
                    backgroundColor: 'rgba(254, 240, 138, 0.5)',
                    pointerEvents: 'none',
                    zIndex: 1000,
                    animation: 'quote-highlight-pulse 0.5s ease-in-out'
                  }}
                />
              )}
            </div>
          </Document>
        </div>
      </div>
    </div>
  );
};
